/**
 * Authentication mutations — §7.3.
 *
 * Each of these is a `useMutation` — with one deliberate exception,
 * `useVerifyEmail`, which is a query because the URL fires it rather than the
 * user, and its single-use token needs its answer cached. The screens read
 * `error` off whichever it is rather than catching: an `ApiError` carries
 * `errors[]`, and the form maps those onto fields (see `useProblemErrors`).
 *
 * Two rules the API imposes that the client has to respect:
 *
 * * **Registration does not log you in.** Verification comes from the email
 *   first, so the screen sends the user to "check your inbox", not to `/`.
 * * **Reset and resend always answer 200**, found or not — anything else is an
 *   account-enumeration oracle. So a success here means "we tried", and the
 *   screen must not claim the address exists.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { api, unwrap } from '@/shared/api/client'
import type { ApiError } from '@/shared/api/problem'
import { queryKeys } from '@/shared/api/queryKeys'
import type { Me } from '@/shared/api/types'
import type { components } from '@/shared/api/schema'

import { beginSignOut, endSignOut } from './navigation'

type Schemas = components['schemas']

export function useLogin(): UseMutationResult<Me, ApiError, Schemas['LoginRequest']> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: Schemas['LoginRequest']) =>
      unwrap(api.POST('/api/v1/auth/login/', { body })),
    // The response *is* `/me/`; seeding the cache with it saves a round trip and
    // stops the first authenticated screen rendering a loading state.
    onSuccess: (me) => queryClient.setQueryData(queryKeys.me, me),
  })
}

export function useLogout(): UseMutationResult<unknown, ApiError, void> {
  const queryClient = useQueryClient()

  return useMutation({
    // ⚠️ **Raised BEFORE the request, not in `onSettled`.** Clearing the
    // session re-renders the current screen, and if it sits behind
    // `RequireAuth` the guard redirects to `/login?next=<that page>` in the
    // same pass. The flag has to be up before anything can clear, or the guard
    // has already captured the page by the time we lower it.
    mutationFn: () => {
      beginSignOut()
      return unwrap(api.POST('/api/v1/auth/logout/'))
    },
    onSettled: () => {
      // ⚠️ **Order matters, and the obvious order is wrong.**
      //
      // `clear()` removes every query *and its observers*, so a
      // `setQueryData` afterwards writes to a key nothing is subscribed to:
      // `SessionProvider`'s `useQuery` never re-renders, and the header keeps
      // showing the signed-out user's name until a hard refresh. The server
      // has already dropped the cookies by then, so the app is in a state no
      // request can correct.
      //
      // Seeding `null` *first* means the provider's observer is reading the
      // anonymous value before anything else changes; `removeQueries` then
      // drops everything except that entry, because whatever the previous
      // user was looking at is still cached and the next person to log in on
      // this browser must not see a frame of it.
      queryClient.setQueryData(queryKeys.me, null)
      queryClient.removeQueries({
        predicate: (query) => query.queryHash !== JSON.stringify(queryKeys.me),
      })

      // ⚠️ **Lowered on a macrotask, not inline.** The guard's redirect and the
      // sign-out handler's own `navigate('/')` both run in renders triggered by
      // the cache writes above; clearing the flag on this line would let the
      // guard see `false` again during that same settle and re-capture the
      // page. Deferring puts it after the navigation has landed. It must be
      // lowered *somewhere*, or the first genuine deep link after a sign-out
      // silently loses its `next` — the opposite bug.
      setTimeout(endSignOut, 0)
    },
  })
}

export function useRegister(): UseMutationResult<Me, ApiError, Schemas['RegisterRequest']> {
  return useMutation({
    mutationFn: (body: Schemas['RegisterRequest']) =>
      unwrap(api.POST('/api/v1/auth/register/', { body })),
  })
}

/**
 * Confirm an email address from the token in the link.
 *
 * ⚠️ **A query, not a mutation — that difference is the whole fix.** Nothing
 * the user does starts this: arriving at the URL does, and the token is
 * single-use. So the *result* has to be remembered against that token, which
 * is what a query cache is and what `useMutation` deliberately is not.
 *
 * `main.tsx` wraps the app in `StrictMode`, so the screen mounts twice. As a
 * mutation fired from an effect, both invocations read the same captured
 * `isIdle` and both fired: the API burnt the token on the first (200) and
 * rejected the second (400), and the screen rendered the later one. A guard
 * that merely suppresses the second call is not enough either — React
 * discards the first instance, so the surviving one has to be able to *find*
 * the answer. Keyed in the cache, it can: the remount subscribes to the same
 * query, joins the in-flight request rather than starting another, and reads
 * the one result.
 *
 * The options are what keep it to one request for the life of the page. A
 * spent token never becomes fresh again, so nothing here is ever stale and no
 * refetch can improve the answer; a 400 is the server's verdict, not a blip,
 * so it must not be retried on failure or on a later mount.
 */
export function useVerifyEmail(token: string | null): UseQueryResult<Me, ApiError> {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: queryKeys.emailVerification(token ?? ''),
    queryFn: async () => {
      const me = await unwrap(
        api.POST('/api/v1/auth/verify-email/', { body: { token: token ?? '' } }),
      )
      // Verification does not create a session, but it does change
      // `is_verified` for anyone already holding one. It sits here rather than
      // in an `onSuccess` — which queries no longer have in v5 — and runs once
      // per token because the query does.
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      return me
    },
    enabled: Boolean(token),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // ⚠️ An *errored* query is stale whatever `staleTime` says: staleness is
    // measured from `dataUpdatedAt`, and a query that only ever errored has
    // none. So window focus is the one refetch trigger `staleTime: Infinity`
    // does not already cover — tabbing away from a spent token and back would
    // fire a second POST at a single-use endpoint. The success path was always
    // safe; this closes the error path.
    refetchOnWindowFocus: false,
    // ⚠️ **`gcTime` is the last hole, and it is not a refetch.** The five
    // options above gate *refetching an entry that exists*; none of them gates
    // the first fetch of an entry that has been garbage-collected. React
    // Query's default `gcTime` is five minutes, so: verify successfully, follow
    // "Sign in instead" (an in-SPA link, no reload), linger on the login screen
    // past that window, then press Back — the entry is gone, the query fires
    // again, and a spent token renders "This link no longer works" to somebody
    // whose account is verified. That is precisely the bug this hook exists to
    // remove, on a five-minute delay. Holding one small entry per token for the
    // life of the page is the cheaper side of that trade.
    gcTime: Infinity,
  })
}

export function useResendVerification(): UseMutationResult<
  unknown,
  ApiError,
  Schemas['ResendVerificationRequest']
> {
  return useMutation({
    mutationFn: (body: Schemas['ResendVerificationRequest']) =>
      unwrap(api.POST('/api/v1/auth/resend-verification/', { body })),
  })
}

export function useRequestPasswordReset(): UseMutationResult<
  unknown,
  ApiError,
  Schemas['PasswordResetRequestRequest']
> {
  return useMutation({
    mutationFn: (body: Schemas['PasswordResetRequestRequest']) =>
      unwrap(api.POST('/api/v1/auth/password/reset/', { body })),
  })
}

export function useConfirmPasswordReset(): UseMutationResult<
  unknown,
  ApiError,
  Schemas['PasswordResetConfirmRequest']
> {
  return useMutation({
    mutationFn: (body: Schemas['PasswordResetConfirmRequest']) =>
      unwrap(api.POST('/api/v1/auth/password/reset/confirm/', { body })),
  })
}

/**
 * The signed-in user's own pending email change, read on mount.
 *
 * `GET /me/email/change/` always answers 200 — `{pending: false}` is a normal
 * result, not an error — so this is an ordinary query with no special retry
 * handling, unlike `useVerifyEmail` below.
 */
export function usePendingEmailChange(): UseQueryResult<
  Schemas['PendingEmailChangeStatus'],
  ApiError
> {
  return useQuery({
    queryKey: queryKeys.pendingEmailChange,
    queryFn: () => unwrap(api.GET('/api/v1/me/email/change/')),
  })
}

/** Request a change to a new address. Seeds the pending-status cache with the response. */
export function useRequestEmailChange(): UseMutationResult<
  Schemas['PendingEmailChangeStatus'],
  ApiError,
  Schemas['EmailChangeRequestRequest']
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: Schemas['EmailChangeRequestRequest']) =>
      unwrap(api.POST('/api/v1/me/email/change/', { body })),
    onSuccess: (status) => queryClient.setQueryData(queryKeys.pendingEmailChange, status),
  })
}

/**
 * Confirm an email change from the token in the link — clicked from the
 * **new** address.
 *
 * A query, not a mutation, for exactly the reason `useVerifyEmail` above is:
 * the click is what fires it, the token is single-use, and `StrictMode`'s
 * double mount must join one in-flight request rather than spend the token
 * twice.
 */
export function useConfirmEmailChange(token: string | null): UseQueryResult<Me, ApiError> {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: queryKeys.emailChangeConfirm(token ?? ''),
    queryFn: async () => {
      const me = await unwrap(
        api.POST('/api/v1/auth/email-change/confirm/', { body: { token: token ?? '' } }),
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      void queryClient.invalidateQueries({ queryKey: queryKeys.pendingEmailChange })
      return me
    },
    enabled: Boolean(token),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

/**
 * Cancel a pending email change from the token in the link — clicked from the
 * **old** address. Same one-shot-query shape as `useConfirmEmailChange`.
 */
export function useCancelEmailChange(token: string | null): UseQueryResult<unknown, ApiError> {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: queryKeys.emailChangeCancel(token ?? ''),
    queryFn: async () => {
      const result = await unwrap(
        api.POST('/api/v1/auth/email-change/cancel/', { body: { token: token ?? '' } }),
      )
      void queryClient.invalidateQueries({ queryKey: queryKeys.pendingEmailChange })
      return result
    },
    enabled: Boolean(token),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}
